import type {
  AppSpacesConfigsPersistence,
  LayoutSection,
  LayoutSpec,
  PanelConfig,
  SpacesSectionConfig,
} from '@src/types/LayoutConfig';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { DEFAULT_MONITORING_LAYOUT_SPEC } from '../../components/AppLayout/Layouts/Monitoring/DefaultMonitoringLayoutSpec';
import { DEFAULT_TRADING_LAYOUT_SPEC } from '../../components/AppLayout/Layouts/Trading/DefaultTradingLayoutSpec';

export interface AppLayoutConfigContextProps {
  selectedLayoutIds: Record<LayoutSection, string>;
  spacesConfigs: AppSpacesConfigsPersistence;
  setLayoutConfig: (section: LayoutSection, value: LayoutSpec) => void;
  setSpacesConfigs: (section: LayoutSection, value: LayoutSpec[]) => void;
  setSelectedLayoutId: (section: LayoutSection, value: string) => void;
  updateLayoutPanelParams: (
    section: LayoutSection,
    layoutId: string,
    panelId: string,
    paramsPatch: Partial<PanelConfig>
  ) => void;
  mergeSpacesSectionConfig: (section: LayoutSection, value: Partial<SpacesSectionConfig>) => void;
  resetLayoutConfig: () => void;
  reImportTabs: () => void;
}

export type LayoutsConfig = Omit<
  AppLayoutConfigContextProps,
  | 'setLayoutConfig'
  | 'resetLayoutConfig'
  | 'reImportTabs'
  | 'setSelectedLayoutId'
  | 'updateLayoutPanelParams'
  | 'setSpacesConfigs'
  | 'mergeSpacesSectionConfig'
>;

export const DefaultLayoutsConfig: LayoutsConfig = {
  spacesConfigs: {
    spacesNavLayoutMigrationVersion: undefined,
    trading: {
      initialized: false,
      layouts: [DEFAULT_TRADING_LAYOUT_SPEC],
    },
    monitoring: {
      initialized: false,
      layouts: [DEFAULT_MONITORING_LAYOUT_SPEC],
    },
  },
  selectedLayoutIds: {
    trading: 'default',
    monitoring: 'default',
  },
} as LayoutsConfig;

/**
 * Provide method for updating AppConfig related to the AppLayout
 */
export const AppLayoutConfigContext = createContext<AppLayoutConfigContextProps>({
  selectedLayoutIds: DefaultLayoutsConfig.selectedLayoutIds,
  spacesConfigs: DefaultLayoutsConfig.spacesConfigs,
  setSelectedLayoutId: () => {},
  updateLayoutPanelParams: () => {},
  setLayoutConfig: () => {},
  setSpacesConfigs: () => {},
  mergeSpacesSectionConfig: () => {},
  resetLayoutConfig: () => {},
  reImportTabs: () => {},
});
AppLayoutConfigContext.displayName = 'AppLayoutConfigContext';

export const useAppLayoutConfig = (
  layoutSection: LayoutSection | undefined
): {
  selectedLayoutId: string | undefined;
  layoutSection: LayoutSection | undefined;
  layoutConfigs: LayoutSpec[] | undefined;
  /** `layoutConfigs` indexed by id — use this instead of converting the array manually. */
  layoutConfigsMap: Record<string, LayoutSpec> | undefined;
  layoutConfig: LayoutSpec | undefined;
  spacesConfigs: SpacesSectionConfig;
  updateLayoutConfig: (layoutConfig: Partial<LayoutSpec> & Pick<LayoutSpec, 'id'>) => void;
  updatePanelParams: (layoutId: string, panelId: string, paramsPatch: Partial<PanelConfig>) => void;
  updateSpacesConfigs: (layoutConfigs: LayoutSpec[]) => void;
  updateSelectedLayoutId: (id: string) => void;
  initializeSpaces: () => void;
  resetLayoutConfig: () => void;
  reImportTabs: () => void;
} => {
  const {
    setLayoutConfig,
    setSpacesConfigs,
    selectedLayoutIds,
    setSelectedLayoutId,
    updateLayoutPanelParams,
    resetLayoutConfig,
    reImportTabs,
    mergeSpacesSectionConfig,
    spacesConfigs,
  } = useContext(AppLayoutConfigContext);

  const updateSpacesConfigs = useCallback(
    (newLayoutConfigs: LayoutSpec[]) => {
      if (layoutSection) {
        setSpacesConfigs(layoutSection, newLayoutConfigs);
      }
    },
    [setSpacesConfigs, layoutSection]
  );

  const updateLayoutConfig = useCallback(
    (layoutConfig: Partial<LayoutSpec> & Pick<LayoutSpec, 'id'>) => {
      if (layoutSection) {
        const prevConfig = spacesConfigs[layoutSection]?.layouts?.find(l => l.id === layoutConfig.id) ?? {};
        const newConfig = { ...prevConfig, ...layoutConfig } as LayoutSpec;
        setLayoutConfig(layoutSection, newConfig);
      }
    },
    [setLayoutConfig, layoutSection, spacesConfigs]
  );

  const selectedLayoutId = layoutSection ? selectedLayoutIds[layoutSection] : undefined;

  const sectionLayoutConfigs = layoutSection ? spacesConfigs[layoutSection]?.layouts : undefined;

  const layoutConfigsMap = useMemo(
    () => (sectionLayoutConfigs ? Object.fromEntries(sectionLayoutConfigs.map(l => [l.id, l])) : undefined),
    [sectionLayoutConfigs]
  );

  const updateSelectedLayoutId = useCallback(
    (id: string) => {
      layoutSection && setSelectedLayoutId(layoutSection, id);
    },
    [layoutSection, setSelectedLayoutId]
  );

  const updatePanelParams = useCallback(
    (layoutId: string, panelId: string, paramsPatch: Partial<PanelConfig>) => {
      if (layoutSection) {
        updateLayoutPanelParams(layoutSection, layoutId, panelId, paramsPatch);
      }
    },
    [layoutSection, updateLayoutPanelParams]
  );

  const initializeSpaces = useCallback(() => {
    if (layoutSection) {
      mergeSpacesSectionConfig(layoutSection, { initialized: true });
    }
  }, [mergeSpacesSectionConfig, layoutSection]);

  return useMemo(
    () => ({
      selectedLayoutId,
      layoutSection,
      layoutConfigs: sectionLayoutConfigs,
      layoutConfigsMap,
      layoutConfig:
        selectedLayoutId && sectionLayoutConfigs
          ? sectionLayoutConfigs.find(l => l.id === selectedLayoutId)
          : undefined,
      spacesConfigs: (layoutSection ? spacesConfigs[layoutSection] : undefined) ?? { initialized: false },
      updateLayoutConfig,
      updatePanelParams,
      updateSpacesConfigs,
      updateSelectedLayoutId,
      resetLayoutConfig,
      reImportTabs,
      initializeSpaces,
    }),
    [
      selectedLayoutId,
      layoutSection,
      sectionLayoutConfigs,
      layoutConfigsMap,
      spacesConfigs,
      updateLayoutConfig,
      updatePanelParams,
      updateSpacesConfigs,
      updateSelectedLayoutId,
      resetLayoutConfig,
      reImportTabs,
      initializeSpaces,
    ]
  );
};
